// Shared utilities for bulk edit tables (purchase_items, transaction_line_items, etc.)

// Helper function to check if element has class
export function hasClass(element, className) {
  return element.classList.contains(className);
}

// Helper function to get closest parent element
export function closest(element, selector) {
  if (element.closest) {
    return element.closest(selector);
  }
  // Fallback for older browsers
  while (element && element.nodeType === 1) {
    if (element.matches && element.matches(selector)) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}

// Helper function to serialize form data
export function serializeForm(form) {
  const formData = new FormData(form);
  const params = new URLSearchParams();
  
  // Add all fields from FormData (includes values)
  const addedFields = new Set();
  for (const [key, value] of formData.entries()) {
    params.append(key, value);
    addedFields.add(key);
  }
  
  // For selects that might be empty, ensure they're included
  // FormData doesn't include selects with no selected option, so we need to add them explicitly
  form.querySelectorAll('select').forEach(select => {
    if (select.name && !select.disabled && !addedFields.has(select.name)) {
      // Select has no value and wasn't included in FormData, add it as empty
      params.append(select.name, '');
    }
  });
  
  return params.toString();
}

// Generic function to initialize select2 with AJAX
export function initializeSchemaAwareSelect(element, options) {
  const defaults = {
    url: null,
    placeholder: 'Select...',
    minimumInputLength: 0,
    allowClear: true
  };
  
  const settings = { ...defaults, ...options };
  
  // Use jQuery for Select2 since it requires jQuery
  if (typeof jQuery !== 'undefined' && jQuery(element).select2) {
    const $select = jQuery(element);
    $select.select2({
      theme: "bootstrap-5",
      ajax: {
        url: settings.url,
        dataType: 'json',
        delay: 250,
        data: function(params) {
          const row = jQuery(this).closest('tr');
          const schemaSelect = row.find('select[name*="[move_to_schema]"]');
          return {
            q: params.term,
            schema: schemaSelect.val()
          };
        },
        processResults: function(data) {
          return {
            results: data
          };
        }
      },
      placeholder: settings.placeholder,
      allowClear: settings.allowClear,
      dropdownAutoWidth : true,
      minimumInputLength: settings.minimumInputLength
    });
    
    // Sync Select2 changes to underlying select element
    $select.on('change', function() {
      const select2Value = $select.val();
      if (select2Value) {
        element.value = select2Value;
      } else {
        // Clear: remove all selected options
        element.selectedIndex = -1;
        // Remove any dynamically added options (keep only placeholder if exists)
        const placeholderOption = Array.from(element.options).find(opt => opt.value === '' || opt.value === null);
        Array.from(element.options).forEach(opt => {
          if (opt.value && opt.value !== '' && opt.selected) {
            opt.remove();
          }
        });
        element.value = '';
      }
    });
    
    // Also sync on clear (select2:clear event)
    $select.on('select2:clear', function() {
      // Remove all selected options
      element.selectedIndex = -1;
      // Remove any dynamically added options
      Array.from(element.options).forEach(opt => {
        if (opt.value && opt.value !== '' && opt.selected) {
          opt.remove();
        }
      });
      element.value = '';
    });
  }
}

// Initialize specific select types based on configuration
export function initializeSelectElement(selectElement, config) {
  // Check if Select2 is available
  if (typeof jQuery === 'undefined' || !jQuery(selectElement).select2) {
    console.warn('Select2 not available for element:', selectElement);
    return;
  }
  
  const selectConfig = config.selectTypes || {};
  
  if (hasClass(selectElement, 'account-select')) {
    initializeSchemaAwareSelect(selectElement, selectConfig.account || {
      url: '/accounts/query_without_add_new',
      placeholder: 'Select Account'
    });
  } else if (hasClass(selectElement, 'employee-select')) {
    initializeSchemaAwareSelect(selectElement, selectConfig.employee || {
      url: '/employees/query_without_add_new',
      placeholder: 'Select Employee'
    });
  } else if (hasClass(selectElement, 'business-division-select')) {
    initializeSchemaAwareSelect(selectElement, selectConfig.businessDivision || {
      url: '/businesses/divisions_query_without_add_new',
      placeholder: 'Select Project'
    });
  } else if (hasClass(selectElement, 'setup-select2')) {
    const $select = jQuery(selectElement);
    $select.select2({
      theme: "bootstrap-5",
      dropdownAutoWidth : true
    });
    
    // Sync Select2 changes to underlying select element
    $select.on('change', function() {
      const select2Value = $select.val();
      if (select2Value) {
        selectElement.value = select2Value;
      } else {
        // Clear: remove all selected options
        selectElement.selectedIndex = -1;
        Array.from(selectElement.options).forEach(opt => {
          if (opt.value && opt.value !== '' && opt.selected) {
            opt.remove();
          }
        });
        selectElement.value = '';
      }
    });
    
    // Also sync on clear
    $select.on('select2:clear', function() {
      selectElement.selectedIndex = -1;
      Array.from(selectElement.options).forEach(opt => {
        if (opt.value && opt.value !== '' && opt.selected) {
          opt.remove();
        }
      });
      selectElement.value = '';
    });
  } else if (hasClass(selectElement, 'product-picker')) {
    const $select = jQuery(selectElement);
    const productConfig = selectConfig.productPicker || {
      url: "/product_types/query_with_add_new",
      fastCreateUrl: "/product_types/fast_create"
    };
    
    $select.select2({
      theme: "bootstrap-5",
      dropdownAutoWidth : true,
      ajax: {
        url: productConfig.url,
        dataType: 'json',
        delay: 250,
        processResults: function(data) {
          return { results: data };
        }
      }
    });
    
    // Sync Select2 changes to underlying select element
    $select.on('change', function() {
      const select2Value = $select.val();
      if (select2Value) {
        selectElement.value = select2Value;
      } else {
        // Clear: remove all selected options
        selectElement.selectedIndex = -1;
        Array.from(selectElement.options).forEach(opt => {
          if (opt.value && opt.value !== '' && opt.selected) {
            opt.remove();
          }
        });
        selectElement.value = '';
      }
    });
    
    // Also sync on clear
    $select.on('select2:clear', function() {
      selectElement.selectedIndex = -1;
      Array.from(selectElement.options).forEach(opt => {
        if (opt.value && opt.value !== '' && opt.selected) {
          opt.remove();
        }
      });
      selectElement.value = '';
    });
    
    $select.on('select2:select', function(e) {
      if (e.params.data.id === '-2') {
        const name = e.params.data.text.split(':').slice(1).join(':').trim();
        fetch(productConfig.fastCreateUrl + "?name=" + encodeURIComponent(name))
          .then(function(response) {
            return response.json();
          })
          .then(function(created_result) {
            if (parseInt(created_result.id) > 0) {
              const newOption = new Option(created_result.name, created_result.id, true, true);
              $select.val(null).append(newOption).trigger('change');
            } else {
              alert(created_result.message);
            }
          })
          .catch(function(error) {
            console.error('Error creating product type:', error);
            alert('An error occurred while creating product type');
          });
      }
    });
  }
}

// Generic calculation function
export function calculateField(element, qtySelector, priceSelector, resultSelector) {
  const row = closest(element, 'tr');
  if (!row) return;
  
  const quantityEl = row.querySelector(qtySelector);
  const priceEl = row.querySelector(priceSelector);
  const resultEl = row.querySelector(resultSelector);
  
  if (!quantityEl || !priceEl || !resultEl) return;
  
  const quantity = parseFloat(quantityEl.value) || 0;
  const price = parseFloat(priceEl.value) || 0;
  const result = quantity * price;
  resultEl.value = result.toFixed(2);
}

// Main BulkEditTable class
export class BulkEditTable {
  constructor(config) {
    this.config = {
      tableId: config.tableId,
      formId: config.formId,
      addRowBtnId: config.addRowBtnId || 'add-row-btn',
      rowPartialPath: config.rowPartialPath,
      schemasToEdit: config.schemasToEdit || [],
      formAction: config.formAction,
      selectTypes: config.selectTypes || {},
      calculationHandlers: config.calculationHandlers || [],
      fieldsToCopy: config.fieldsToCopy || [],
      destroyInputSelector: config.destroyInputSelector,
      topRowClass: config.topRowClass || 'split-row-top',
      bottomRowClass: config.bottomRowClass || 'split-row-bottom',
      rowClass: config.rowClass || 'purchase-item-row',
      highlightClass: config.highlightClass || 'row-highlight',
      // Sorting configuration
      sorting: config.sorting || {
        enabled: false,
        numericColumns: [],
        dateColumns: [],
        columnMapping: null // Function to map column name to cell position
      },
      // Toggle configuration
      toggles: config.toggles || {
        approval: { enabled: false, inputIdPrefix: 'is_approved_' },
        transaction: { enabled: false, inputIdPrefix: 'add_transaction_' }
      }
    };
    
    this.addNewRowInProgress = false;
    this.readyCalled = false;
    // Track current sort state
    this.currentSortColumn = null;
    this.currentSortDirection = null;
    // Track form submission state (class-level to prevent duplicates across handler re-attachments)
    this.isFormSubmitting = false;
  }

  init() {
    // Always clean up existing Select2 instances before re-initializing
    // This handles both Turbo navigation and browser back button (page cache restore)
    this.cleanupSelect2();
    this.readyCalled = true;
    
    // Clear handler attachment flags to allow re-attachment after browser back button
    // (page cache restore preserves DOM but not JavaScript event listeners)
    const addRowBtn = document.getElementById(this.config.addRowBtnId);
    if (addRowBtn) {
      delete addRowBtn.dataset.handlerAttached;
    }
    const table = document.getElementById(this.config.tableId);
    if (table) {
      delete table.dataset.handlersAttached;
      delete table.dataset.sortHandlersAttached;
    }
    const form = document.getElementById(this.config.formId);
    if (form) {
      delete form.dataset.handlerAttached;
    }
    
    this.setupSelect2();
    this.setupCalculations();
    this.setupAddRow();
    this.setupTableHandlers();
    this.setupFormSubmission();
    
    // Setup sorting if enabled
    if (this.config.sorting && this.config.sorting.enabled) {
      this.setupSorting();
      // Check if there's an initial sort state from the page
      this.detectInitialSortState();
    }
  }

  // Detect initial sort state from the table headers
  detectInitialSortState() {
    const table = document.getElementById(this.config.tableId);
    if (!table) return;
    
    // First, check for explicit sort classes on headers
    const sortedHeader = table.querySelector('th.sortable.asc, th.sortable.desc');
    if (sortedHeader) {
      const column = sortedHeader.getAttribute('data-sort');
      const direction = sortedHeader.classList.contains('asc') ? 'asc' : 'desc';
      this.currentSortColumn = column;
      this.currentSortDirection = direction;
      return;
    }
    
    // If no explicit sort classes, check for data attributes on the table
    const sortColumn = table.getAttribute('data-sort-column');
    const sortDirection = table.getAttribute('data-sort-direction');
    if (sortColumn && sortDirection) {
      this.currentSortColumn = sortColumn;
      this.currentSortDirection = sortDirection;
      // Also apply the visual indicator
      const header = table.querySelector(`th.sortable[data-sort="${sortColumn}"]`);
      if (header) {
        header.classList.add(sortDirection);
      }
      return;
    }
    
    // If still no sort detected, check if there's a default sort in the config
    if (this.config.sorting && this.config.sorting.defaultColumn && this.config.sorting.defaultDirection) {
      this.currentSortColumn = this.config.sorting.defaultColumn;
      this.currentSortDirection = this.config.sorting.defaultDirection;
      // Apply the visual indicator
      const header = table.querySelector(`th.sortable[data-sort="${this.config.sorting.defaultColumn}"]`);
      if (header) {
        header.classList.add(this.config.sorting.defaultDirection);
      }
      // Don't re-sort here - the data is already sorted by the server
      // We just need to track the sort state so it can be re-applied after adding rows
    }
  }

  cleanupSelect2() {
    // Destroy all Select2 instances in the table to allow re-initialization
    // BUT skip elements handled by Stimulus controller (business-division-picker, etc.)
    const table = document.getElementById(this.config.tableId);
    if (table) {
      table.querySelectorAll('select').forEach((select) => {
        // Skip business-division-picker and other Stimulus-handled elements
        if (select.classList.contains('business-division-picker') || 
            select.classList.contains('select2-initialized')) {
          return;
        }
        
        if (typeof jQuery !== 'undefined' && jQuery(select).data('select2')) {
          try {
            // Destroy the Select2 instance
            jQuery(select).select2('destroy');
            // Remove the Select2 container (usually a sibling span element)
            const container = select.nextElementSibling;
            if (container && container.classList.contains('select2-container')) {
              container.remove();
            }
          } catch (e) {
            // Select2 might already be destroyed, ignore errors
            console.warn('Error destroying Select2:', e);
          }
        }
        // Reset data attributes to allow re-initialization
        delete select.dataset.schemaChangeHandler;
      });
    }
  }

  setupSelect2() {
    // Handle schema change to clear dependent selects
    const schemaSelects = document.querySelectorAll('select[name*="[move_to_schema]"]');
    schemaSelects.forEach((select) => {
      // Use a data attribute to prevent duplicate listeners
      if (!select.dataset.schemaChangeHandler) {
        select.dataset.schemaChangeHandler = 'true';
        select.addEventListener('change', () => {
          const row = closest(select, 'tr');
          if (row) {
            const dependentSelects = row.querySelectorAll('select[name*="[account_id]"], select[name*="[employee_id]"], select[name*="[business_division_id]"]');
            dependentSelects.forEach((depSelect) => {
              depSelect.value = '';
              // Trigger change event for Select2
              if (typeof jQuery !== 'undefined' && jQuery(depSelect).data('select2')) {
                jQuery(depSelect).trigger('change');
              } else {
                depSelect.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
        });
      }
    });

    // Initialize all selects for existing rows (cleanupSelect2 should have destroyed existing instances)
    const table = document.getElementById(this.config.tableId);
    if (table) {
      // Initialize schema-aware selects
      table.querySelectorAll('.account-select').forEach((el) => {
        initializeSchemaAwareSelect(el, this.config.selectTypes.account || {
          url: '/accounts/query_without_add_new',
          placeholder: 'Select Account'
        });
      });
      table.querySelectorAll('.employee-select').forEach((el) => {
        initializeSchemaAwareSelect(el, this.config.selectTypes.employee || {
          url: '/employees/query_without_add_new',
          placeholder: 'Select Employee'
        });
      });
      table.querySelectorAll('.business-division-select').forEach((el) => {
        initializeSchemaAwareSelect(el, this.config.selectTypes.businessDivision || {
          url: '/businesses/divisions_query_without_add_new',
          placeholder: 'Select Project'
        });
      });
      
      // Initialize all other Select2 selects (setup-select2, product-picker, etc.)
      // Exclude business-division-picker which is handled by Stimulus controller
      table.querySelectorAll('select.setup-select2, select.product-picker').forEach((el) => {
        initializeSelectElement(el, this.config);
      });
      
      // For elements with data-controller="select2", only initialize if not business-division-picker
      // (business-division-picker is handled by Stimulus controller)
      table.querySelectorAll('select[data-controller="select2"]:not(.business-division-picker)').forEach((el) => {
        // Only initialize if not already initialized by Stimulus
        if (!el.classList.contains('select2-initialized')) {
          initializeSelectElement(el, this.config);
        }
      });
    }
  }

  setupCalculations() {
    const form = document.getElementById(this.config.formId);
    if (!form) return;

    form.addEventListener('change', (e) => {
      this.config.calculationHandlers.forEach((handler) => {
        if (e.target.classList.contains(handler.triggerClass)) {
          calculateField(
            e.target,
            handler.qtySelector,
            handler.priceSelector,
            handler.resultSelector
          );
        }
      });
    });
  }

  addNewRow(insertAfterRow = null, insertBefore = false) {
    // Prevent multiple simultaneous calls
    if (this.addNewRowInProgress) {
      return null;
    }
    this.addNewRowInProgress = true;
    
    const tbody = document.querySelector(`#${this.config.tableId} tbody`);
    if (!tbody) {
      this.addNewRowInProgress = false;
      return null;
    }
    
    const newId = 'new_' + Date.now();
    
    // Get row HTML from the config (should be set via setRowHtmlTemplate)
    if (!this.rowHtmlTemplate) {
      console.error('Row HTML template not set. Call setRowHtmlTemplate() first.');
      this.addNewRowInProgress = false;
      return null;
    }
    
    let newRowHtml = this.rowHtmlTemplate.replace(/NEW_ID_PLACEHOLDER/g, newId);
    
    // Insert the HTML directly into the tbody
    if (insertAfterRow) {
      if (insertBefore) {
        insertAfterRow.insertAdjacentHTML('beforebegin', newRowHtml);
      } else {
        insertAfterRow.insertAdjacentHTML('afterend', newRowHtml);
      }
    } else {
      // For fast_transactions, insert at the top to maintain date DESC sort order
      // New rows should appear at the top since they're the newest
      const firstRow = tbody.querySelector('tr');
      if (firstRow) {
        firstRow.insertAdjacentHTML('beforebegin', newRowHtml);
      } else {
        tbody.insertAdjacentHTML('beforeend', newRowHtml);
      }
    }
    
    // Reset the flag after a short delay
    setTimeout(() => {
      this.addNewRowInProgress = false;
    }, 100);
    
    // Initialize Select2 for the newly added select elements
    // Only initialize selects that don't already have Select2 initialized
    // Note: data-id might include schema prefix (e.g., "17_new_123"), so we need to find rows that end with newId
    const allRows = tbody.querySelectorAll('tr[data-id]');
    const newRowsWithId = Array.from(allRows).filter(row => {
      const dataId = row.getAttribute('data-id');
      return dataId && (dataId === newId || dataId.endsWith('_' + newId));
    });
    
    // For fast_transactions: If account filter is set, automatically select it in the new row
    const accountFilter = document.querySelector('select[name="account_id"]');
    const filterAccountId = accountFilter && accountFilter.value ? accountFilter.value : null;
    
    newRowsWithId.forEach((row) => {
      const selects = row.querySelectorAll('select.form-select');
      selects.forEach((select) => {
        // Skip if Select2 is already initialized
        if (typeof jQuery !== 'undefined' && jQuery(select).data('select2')) {
          return;
        }
        
        // Check if Select2 is available
        if (typeof jQuery !== 'undefined' && jQuery(select).select2) {
          initializeSelectElement(select, this.config);
        } else {
          // Select2 not ready yet, try again after a short delay
          setTimeout(() => {
            // Check again if it's been initialized in the meantime
            if (!jQuery(select).data('select2')) {
              initializeSelectElement(select, this.config);
            }
          }, 100);
        }
      });
      
      // Set account from filter if available (after a short delay to allow Select2 to initialize)
      if (filterAccountId) {
        const accountSelect = row.querySelector(`select[name*="[account_id]"]`);
        if (accountSelect && !accountSelect.disabled && accountSelect.classList.contains('account-select')) {
          // Get the account name from the filter select
          const filterSelect = document.querySelector('select[name="account_id"]');
          const selectedOption = filterSelect ? filterSelect.options[filterSelect.selectedIndex] : null;
          const accountName = selectedOption ? selectedOption.text : '';
          
          // Create an option element with the account_id and name
          const option = document.createElement('option');
          option.value = filterAccountId;
          option.text = accountName;
          option.selected = true;
          
          // Clear existing options (except placeholder) and add the new option
          Array.from(accountSelect.options).forEach(opt => {
            if (opt.value && opt.value !== '') {
              opt.remove();
            }
          });
          accountSelect.appendChild(option);
          accountSelect.value = filterAccountId;
          
          // Update Select2 after initialization (with a delay to ensure Select2 is ready)
          setTimeout(() => {
            if (typeof jQuery !== 'undefined' && jQuery(accountSelect).data('select2')) {
              // Set the value in Select2
              jQuery(accountSelect).val(filterAccountId).trigger('change');
            }
          }, 300);
        }
      }
    });
    
    // Apply highlight effect
    const newTopRow = Array.from(allRows).find(row => {
      const dataId = row.getAttribute('data-id');
      return dataId && (dataId === newId || dataId.endsWith('_' + newId)) && row.classList.contains(this.config.topRowClass);
    });
    const newBottomRow = Array.from(allRows).find(row => {
      const dataId = row.getAttribute('data-id');
      return dataId && (dataId === newId || dataId.endsWith('_' + newId)) && row.classList.contains(this.config.bottomRowClass);
    });
    
    if (newTopRow) {
      newTopRow.classList.add(this.config.highlightClass);
    }
    if (newBottomRow) {
      newBottomRow.classList.add(this.config.highlightClass);
    }
    
    // Remove highlight after 5 seconds
    setTimeout(() => {
      if (newTopRow) {
        newTopRow.classList.remove(this.config.highlightClass);
      }
      if (newBottomRow) {
        newBottomRow.classList.remove(this.config.highlightClass);
      }
    }, 2500);
    
    // Re-apply current sort if one is active
    // But skip if it's the default sort and we just inserted at the top (to maintain order)
    // The server already sorted the data correctly, and new rows are inserted at the top
    const isDefaultSort = this.config.sorting && 
                         this.config.sorting.defaultColumn &&
                         this.config.sorting.defaultDirection &&
                         this.config.sorting.defaultColumn === this.currentSortColumn &&
                         this.config.sorting.defaultDirection === this.currentSortDirection;
    
    // Only re-sort if:
    // 1. Sorting is enabled
    // 2. There's an active sort (not just default)
    // 3. The sort is NOT the default sort (default sort is already correct from server)
    if (this.config.sorting && this.config.sorting.enabled && 
        this.currentSortColumn && this.currentSortDirection && 
        !isDefaultSort) {
      setTimeout(() => {
        this.sortTable(this.currentSortColumn, this.currentSortDirection);
      }, 25);
    }
    
    return newRowsWithId;
  }

  setupAddRow() {
    const addRowBtn = document.getElementById(this.config.addRowBtnId);
    if (addRowBtn && !addRowBtn.dataset.handlerAttached) {
      addRowBtn.dataset.handlerAttached = 'true';
      addRowBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addNewRow();
      });
    }
  }

  setupTableHandlers() {
    const table = document.getElementById(this.config.tableId);
    if (!table || table.dataset.handlersAttached) return;
    
    table.dataset.handlersAttached = 'true';
    
    table.addEventListener('click', (e) => {
      // Handle delete row
      if (e.target.closest('.delete-row-btn')) {
        this.handleDeleteRow(e);
      }
      
      // Handle copy row
      if (e.target.closest('.copy-row-btn')) {
        this.handleCopyRow(e);
      }
    });
  }

  handleDeleteRow(e) {
    const btn = e.target.closest('.delete-row-btn');
    const row = closest(btn, 'tr');
    if (!row) return;
    
    const prevRow = row.previousElementSibling;
    const nextRow = row.nextElementSibling;
    
    // Check if this is a split-row structure (top and bottom rows)
    const isTopRow = this.config.topRowClass && row.classList.contains(this.config.topRowClass);
    const isBottomRow = this.config.bottomRowClass && row.classList.contains(this.config.bottomRowClass);
    
    // Determine which rows to delete
    let rowsToDelete = [row];
    
    if (isTopRow && nextRow && this.config.bottomRowClass && nextRow.classList.contains(this.config.bottomRowClass)) {
      // If deleting top row, also delete the bottom row
      rowsToDelete.push(nextRow);
    } else if (isBottomRow && prevRow && this.config.topRowClass && prevRow.classList.contains(this.config.topRowClass)) {
      // If deleting bottom row, also delete the top row
      rowsToDelete.push(prevRow);
    }
    
    // Mark for deletion - find destroy input in the top row (where the delete button is)
    const topRow = isTopRow ? row : (isBottomRow && prevRow ? prevRow : row);
    const destroyInput = topRow?.querySelector(this.config.destroyInputSelector);
    if (destroyInput) {
      destroyInput.value = 'true';
    }
    
    // Remove any validation error rows associated with these rows
    rowsToDelete.forEach((rowToDelete) => {
      // Find the bottom row (where error messages are attached)
      const bottomRow = rowToDelete.classList.contains(this.config.bottomRowClass) 
        ? rowToDelete 
        : (rowToDelete.nextElementSibling && rowToDelete.nextElementSibling.classList.contains(this.config.bottomRowClass)
          ? rowToDelete.nextElementSibling 
          : null);
      
      if (bottomRow) {
        // Check if there's a validation error row after the bottom row
        let nextSibling = bottomRow.nextElementSibling;
        if (nextSibling && nextSibling.classList.contains('validation-error-row')) {
          nextSibling.remove();
        }
      }
      
      // Also check if the row itself is an error row
      if (rowToDelete.classList.contains('validation-error-row')) {
        rowToDelete.remove();
      }
    });
    
    // Check if any row has a dataset.id (existing record)
    const hasExistingRecord = rowsToDelete.some(r => r && r.dataset && r.dataset.id);
    
    if (hasExistingRecord) {
      // Hide rows for existing records (they'll be marked for deletion on submit)
      rowsToDelete.forEach(r => {
        if (r) r.style.display = 'none';
      });
    } else {
      // For new rows, destroy select2 instances and disconnect Stimulus controllers before removing
      rowsToDelete.forEach((r) => {
        if (r) {
          r.querySelectorAll('select').forEach((select) => {
            if (typeof jQuery !== 'undefined' && jQuery(select).data('select2')) {
              jQuery(select).select2('destroy');
            }
            // Disconnect Stimulus controller
            if (select.select2Controller) {
              select.select2Controller.disconnect();
            }
          });
          r.remove();
        }
      });
    }
  }

  handleCopyRow(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const btn = e.target.closest('.copy-row-btn');
    
    // Prevent multiple clicks
    if (btn.dataset.copying === 'true') return;
    btn.dataset.copying = 'true';
    
    // Find the row containing the button (could be top or bottom row)
    const buttonRow = closest(btn, 'tr');
    if (!buttonRow) {
      btn.dataset.copying = 'false';
      return;
    }
    
    // Determine if button is in top or bottom row
    const isTopRow = buttonRow.classList.contains(this.config.topRowClass);
    const isBottomRow = buttonRow.classList.contains(this.config.bottomRowClass);
    
    let sourceTopRow, sourceBottomRow;
    
    if (isTopRow) {
      // Button is in top row, bottom row is next sibling
      sourceTopRow = buttonRow;
      sourceBottomRow = buttonRow.nextElementSibling;
    } else if (isBottomRow) {
      // Button is in bottom row, top row is previous sibling
      sourceTopRow = buttonRow.previousElementSibling;
      sourceBottomRow = buttonRow;
    } else {
      // Can't determine row structure
      btn.dataset.copying = 'false';
      return;
    }
    
    if (!sourceTopRow || !sourceBottomRow) {
      btn.dataset.copying = 'false';
      return;
    }
    
    // Add new row after the bottom row (to maintain two-row structure)
    const newRows = this.addNewRow(sourceBottomRow);
    if (!newRows || newRows.length === 0) {
      btn.dataset.copying = 'false';
      return;
    }
    
    // Reset the flag after a short delay
    setTimeout(() => {
      btn.dataset.copying = 'false';
    }, 500);
    
    const newTopRow = Array.from(newRows).find((row) => {
      return row.classList.contains(this.config.topRowClass);
    });
    const newBottomRow = Array.from(newRows).find((row) => {
      return row.classList.contains(this.config.bottomRowClass);
    });
    
    // Copy values from the source row to the new row
    this.config.fieldsToCopy.forEach((field) => {
      this.copyField(field, sourceTopRow, sourceBottomRow, newTopRow, newBottomRow);
    });
    
    // Remove any line_item ID fields from the new row to ensure it's treated as a new record
    // This prevents duplicates when copying existing transactions
    if (newBottomRow) {
      // Find all hidden inputs that contain transaction_line_items_attributes and [id]
      const lineItemIdInputs = newBottomRow.querySelectorAll('input[type="hidden"][name*="[transaction_line_items_attributes]"][name*="[id]"]');
      lineItemIdInputs.forEach(input => {
        input.remove();
      });
      
      // Also check in the top row just in case
      if (newTopRow) {
        const topRowLineItemIdInputs = newTopRow.querySelectorAll('input[type="hidden"][name*="[transaction_line_items_attributes]"][name*="[id]"]');
        topRowLineItemIdInputs.forEach(input => {
          input.remove();
        });
      }
    }
  }

  copyField(field, sourceTopRow, sourceBottomRow, newTopRow, newBottomRow) {
    // Handle both string and object field configurations
    let fieldName, fieldType;
    if (typeof field === 'string') {
      fieldName = field;
      fieldType = 'auto';
    } else if (typeof field === 'object' && field.name) {
      fieldName = field.name;
      fieldType = field.type || 'auto';
    } else {
      return; // Invalid field configuration
    }
    
    // Determine field type
    let actualType = fieldType;
    if (actualType === 'auto') {
      if (fieldName.includes('date') || fieldName === 'request_date') {
        actualType = 'date';
      } else if (['quantity_in', 'price_in', 'quantity_out', 'price_out', 'description', 'quantity', 'price', 'total', 'actual_quantity', 'actual_price', 'actual_total'].includes(fieldName)) {
        actualType = 'text';
      } else {
        actualType = 'select';
      }
    }
    
    if (actualType === 'toggle') {
      // Handle toggle button fields (like is_approved, add_transaction)
      // These have hidden inputs and buttons with data attributes in the BOTTOM row
      const sourceBottomRowForToggle = sourceBottomRow;
      const newBottomRowForToggle = newBottomRow;
      
      // Find the hidden input by name pattern in the bottom row
      const sourceHiddenInput = sourceBottomRowForToggle.querySelector(`input[name*="[${fieldName}]"]`);
      const newHiddenInput = newBottomRowForToggle ? newBottomRowForToggle.querySelector(`input[name*="[${fieldName}]"]`) : null;
      
      if (sourceHiddenInput && newHiddenInput) {
        const sourceValue = sourceHiddenInput.value;
        newHiddenInput.value = sourceValue;
        
        // Find and update the toggle button in the bottom row
        const dataAttrName = fieldName === 'is_approved' ? 'data-approved' : 'data-transaction';
        const sourceButton = sourceBottomRowForToggle.querySelector(`button[${dataAttrName}]`);
        const newButton = newBottomRowForToggle ? newBottomRowForToggle.querySelector(`button[${dataAttrName}]`) : null;
        
        if (sourceButton && newButton) {
          // Copy the data attribute
          const sourceDataValue = sourceButton.getAttribute(dataAttrName);
          newButton.setAttribute(dataAttrName, sourceDataValue);
          
          // Update the icon classes
          const sourceIcon = sourceButton.querySelector('i');
          const newIcon = newButton.querySelector('i');
          
          if (sourceIcon && newIcon) {
            // Remove existing classes
            newIcon.classList.remove('fa-circle', 'fa-circle-check', 'text-success', 'text-muted');
            // Copy all classes from source icon
            Array.from(sourceIcon.classList).forEach((cls) => {
              newIcon.classList.add(cls);
            });
          }
        }
      }
    } else if (actualType === 'date') {
      const sourceInput = sourceTopRow.querySelector(`input[name*="[${fieldName}]"]`);
      const newInput = newTopRow ? newTopRow.querySelector(`input[name*="[${fieldName}]"]`) : null;
      if (sourceInput && newInput) {
        newInput.value = sourceInput.value;
      }
    } else if (actualType === 'text') {
      const sourceInput = sourceBottomRow.querySelector(`input[name*="[${fieldName}]"]`);
      const newInput = newBottomRow ? newBottomRow.querySelector(`input[name*="[${fieldName}]"]`) : null;
      if (sourceInput && newInput) {
        newInput.value = sourceInput.value;
        // Trigger change event to recalculate if needed
        if (newInput.classList.contains('update_in') || newInput.classList.contains('update_out') || 
            newInput.classList.contains('update_total') || newInput.classList.contains('update_actual_total')) {
          newInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } else {
      // Handle select fields - check top row first, then bottom row
      let sourceSelect = sourceTopRow.querySelector(`select[name*="[${fieldName}]"]`);
      let newSelect = newTopRow ? newTopRow.querySelector(`select[name*="[${fieldName}]"]`) : null;
      
      // If not found in top row, check bottom row
      if (!sourceSelect || !newSelect) {
        sourceSelect = sourceBottomRow.querySelector(`select[name*="[${fieldName}]"]`);
        newSelect = newBottomRow ? newBottomRow.querySelector(`select[name*="[${fieldName}]"]`) : null;
      }
      
      if (sourceSelect && newSelect) {
        const sourceValue = sourceSelect.value;
        const selectedOption = sourceSelect.options[sourceSelect.selectedIndex];
        const sourceText = selectedOption ? selectedOption.text : '';
        
        if (sourceValue) {
          const newOption = new Option(sourceText, sourceValue, true, true);
          newSelect.appendChild(newOption);
          // Trigger change event for Select2
          if (typeof jQuery !== 'undefined' && jQuery(newSelect).data('select2')) {
            jQuery(newSelect).trigger('change');
          } else {
            newSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
  }

  setupFormSubmission() {
    const form = document.getElementById(this.config.formId);
    if (!form) return;
    
    // Remove any existing submit handler to prevent duplicates (important for Turbo navigation)
    if (form.dataset.handlerAttached && this._formSubmitHandler) {
      form.removeEventListener('submit', this._formSubmitHandler);
    }
    
    let isSubmitting = false;
    
    // Create the submit handler function
    this._formSubmitHandler = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation(); // Prevent other handlers from firing
      
      // Prevent multiple simultaneous submissions (check both local and class-level flag)
      if (isSubmitting || this.isFormSubmitting) {
        console.warn('Form already submitting, skipping');
        return false;
      }
      isSubmitting = true;
      this.isFormSubmitting = true;
      
      // Clear any previous validation errors before submitting
      this.clearValidationErrors();
      
      // Sync Select2 values to underlying select elements before submission
      // This ensures cleared values are properly captured
      if (typeof jQuery !== 'undefined') {
        const table = document.getElementById(this.config.tableId);
        if (table) {
          table.querySelectorAll('select').forEach((select) => {
            if (jQuery(select).data('select2')) {
              // Get the current Select2 value (which may be null/empty if cleared)
              const select2Value = jQuery(select).val();
              if (select2Value) {
                select.value = select2Value;
              } else {
                // Clear: remove selected options and set to empty
                select.selectedIndex = -1;
                Array.from(select.options).forEach(opt => {
                  if (opt.value && opt.value !== '' && opt.selected) {
                    opt.remove();
                  }
                });
                select.value = '';
                // Force the select to have no value by removing all selected options
                Array.from(select.options).forEach(opt => {
                  opt.selected = false;
                });
              }
            }
          });
        }
      }
      
      const formData = serializeForm(form);
      const formAction = this.config.formAction || form.getAttribute('action');
      
      fetch(formAction, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          'Accept': 'application/json'
        },
        body: formData
      })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        isSubmitting = false;
        this.isFormSubmitting = false;
        if (data.success) {
          // Clear any previous validation errors
          this.clearValidationErrors();
          window.location.reload();
        } else {
          // Check if there are validation errors
          if (data.validation_errors && Object.keys(data.validation_errors).length > 0) {
            this.displayValidationErrors(data.validation_errors);
          } else {
            alert(data.error || 'An error occurred');
          }
        }
      })
      .catch((error) => {
        isSubmitting = false;
        this.isFormSubmitting = false;
        console.error('Error:', error);
        alert('An error occurred while saving');
      });
    };
    
    // Attach the handler
    form.addEventListener('submit', this._formSubmitHandler);
    form.dataset.handlerAttached = 'true';
  }

  // Method to set the row HTML template (called from the view)
  setRowHtmlTemplate(template) {
    this.rowHtmlTemplate = template;
  }

  // Display validation errors for specific rows
  displayValidationErrors(validationErrors) {
    const table = document.getElementById(this.config.tableId);
    if (!table) return;

    // Clear any previous errors first
    this.clearValidationErrors();

    // Process each item with errors
    Object.keys(validationErrors).forEach((itemId) => {
      const errors = validationErrors[itemId];
      
      // Find the row(s) for this item ID
      // The row has data-id attribute matching the item ID
      const rows = table.querySelectorAll(`tr[data-id="${itemId}"]`);
      
      if (rows.length > 0) {
        // Highlight the row(s) with errors
        rows.forEach((row) => {
          row.classList.add('table-danger');
          row.style.backgroundColor = '#f8d7da';
        });

        // Create error message element
        const errorMessages = [];
        Object.keys(errors).forEach((field) => {
          const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
          fieldErrors.forEach((errorMsg) => {
            errorMessages.push(`${field === 'base' ? '' : field + ': '}${errorMsg}`);
          });
        });

        // Find the bottom row (where action buttons are) to add error message
        const bottomRow = Array.from(rows).find(row => row.classList.contains(this.config.bottomRowClass));
        if (bottomRow) {
          // Check if error message row already exists (look for it after the bottom row)
          let errorRow = bottomRow.nextElementSibling;
          if (!errorRow || !errorRow.classList.contains('validation-error-row')) {
            // Create a new row for the error message
            errorRow = document.createElement('tr');
            errorRow.className = 'validation-error-row';
            errorRow.style.backgroundColor = '#f8d7da';
            
            // Create a cell that spans all columns
            const errorCell = document.createElement('td');
            errorCell.className = 'validation-error-cell';
            errorCell.colSpan = bottomRow.cells.length || 10; // Use actual cell count or default to 10
            errorCell.style.backgroundColor = '#f8d7da';
            errorRow.appendChild(errorCell);
            
            // Insert the error row after the bottom row
            bottomRow.parentNode.insertBefore(errorRow, bottomRow.nextSibling);
          }
          
          // Get the error cell from the error row
          const errorCell = errorRow.querySelector('.validation-error-cell');
          if (errorCell) {
            // Create or update the error message div
            let errorDiv = errorCell.querySelector('.validation-error-message');
            if (!errorDiv) {
              errorDiv = document.createElement('div');
              errorDiv.className = 'validation-error-message alert alert-danger alert-sm mb-0';
              errorDiv.style.fontSize = '0.875rem';
              errorDiv.style.padding = '0.5rem';
              errorCell.appendChild(errorDiv);
            }
            
            errorDiv.innerHTML = '<strong>Errors:</strong> ' + errorMessages.join('; ');
          }
        }

        // Also highlight specific fields with errors
        Object.keys(errors).forEach((field) => {
          if (field !== 'base') {
            // Find input/select fields for this field name
            rows.forEach((row) => {
              const fieldInput = row.querySelector(`input[name*="[${field}]"], select[name*="[${field}]"]`);
              if (fieldInput) {
                fieldInput.classList.add('is-invalid');
                // Add Bootstrap invalid feedback if not present
                let feedback = fieldInput.parentElement.querySelector('.invalid-feedback');
                if (!feedback) {
                  feedback = document.createElement('div');
                  feedback.className = 'invalid-feedback';
                  fieldInput.parentElement.appendChild(feedback);
                }
                const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
                feedback.textContent = fieldErrors.join(', ');
              }
            });
          }
        });
      }
    });

    // Scroll to first error
    const firstErrorRow = table.querySelector('tr.table-danger');
    if (firstErrorRow) {
      firstErrorRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  // Clear all validation error styling and messages
  clearValidationErrors() {
    const table = document.getElementById(this.config.tableId);
    if (!table) return;

    // Remove error highlighting from rows
    table.querySelectorAll('tr.table-danger').forEach((row) => {
      row.classList.remove('table-danger');
      row.style.backgroundColor = '';
    });

    // Remove error messages and error rows
    table.querySelectorAll('.validation-error-row').forEach((errorRow) => {
      errorRow.remove();
    });
    
    table.querySelectorAll('.validation-error-message').forEach((errorDiv) => {
      errorDiv.remove();
    });

    // Remove field-level error styling
    table.querySelectorAll('.is-invalid').forEach((field) => {
      field.classList.remove('is-invalid');
    });

    // Remove invalid feedback elements
    table.querySelectorAll('.invalid-feedback').forEach((feedback) => {
      feedback.remove();
    });
  }

  // Sorting functionality
  setupSorting() {
    const table = document.getElementById(this.config.tableId);
    if (!table || table.dataset.sortHandlersAttached) return;
    
    table.dataset.sortHandlersAttached = 'true';
    
    table.querySelectorAll('th.sortable').forEach(header => {
      header.addEventListener('click', (e) => {
        // Prevent event from bubbling if needed
        e.stopPropagation();
        
        const column = header.getAttribute('data-sort');
        
        // Skip if no data-sort attribute
        if (!column || column.trim() === '') {
          return;
        }
        
        const isAsc = header.classList.contains('asc');
        
        // Remove all sort classes
        table.querySelectorAll('th').forEach(th => {
          th.classList.remove('asc', 'desc');
        });
        
        // Add new sort class
        header.classList.add(isAsc ? 'desc' : 'asc');
        
        // Sort the table
        const newDirection = isAsc ? 'desc' : 'asc';
        this.currentSortColumn = column;
        this.currentSortDirection = newDirection;
        this.sortTable(column, newDirection);
      });
    });
  }

  sortTable(column, direction) {
    const table = document.getElementById(this.config.tableId);
    const tbody = table.querySelector('tbody');
    const rows = Array.from(tbody.querySelectorAll(`tr.${this.config.topRowClass}`));
    
    rows.sort((a, b) => {
      let aVal = this.getCellValue(a, column);
      let bVal = this.getCellValue(b, column);
      
      const sortConfig = this.config.sorting;
      
      // Handle numeric sorting
      if (sortConfig.numericColumns && sortConfig.numericColumns.includes(column)) {
        // Remove commas and other formatting for numeric parsing
        aVal = parseFloat(String(aVal).replace(/[^\d.-]/g, '')) || 0;
        bVal = parseFloat(String(bVal).replace(/[^\d.-]/g, '')) || 0;
      }
      // Handle date sorting
      else if (sortConfig.dateColumns && sortConfig.dateColumns.includes(column)) {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }
      // Handle string sorting (case-insensitive)
      else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      // Proper comparison with equality handling
      let comparison = 0;
      if (aVal > bVal) {
        comparison = 1;
      } else if (aVal < bVal) {
        comparison = -1;
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });

    // Reorder rows (preserve two-row structure)
    rows.forEach(row => {
      const bottomRow = row.nextElementSibling;
      tbody.appendChild(row);
      if (bottomRow) {
        tbody.appendChild(bottomRow);
      }
    });
  }

  getCellValue(row, column) {
    const cell = this.findCell(row, column);
    if (!cell) return '';

    // Handle select elements
    const select = cell.querySelector('select');
    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      return selectedOption ? selectedOption.text : '';
    }

    // Handle input elements - prefer visible/non-hidden inputs
    const inputs = cell.querySelectorAll('input');
    if (inputs.length > 0) {
      // Find the first visible, non-hidden input
      for (let input of inputs) {
        if (input.type !== 'hidden' && input.offsetParent !== null) {
          return input.value;
        }
      }
      // If no visible input found, use the first input
      return inputs[0].value;
    }

    // Handle label elements (for balance column)
    const label = cell.querySelector('label');
    if (label) {
      return label.textContent.trim();
    }

    // Default to cell text content
    return cell.textContent.trim();
  }

  findCell(row, column) {
    if (this.config.sorting.columnMapping && typeof this.config.sorting.columnMapping === 'function') {
      return this.config.sorting.columnMapping(row, column);
    }
    return null;
  }

  // Toggle functions
  toggleApproval(button, itemId) {
    if (!this.config.toggles.approval.enabled) return;
    
    const isApproved = button.getAttribute('data-approved') === 'false';
    button.setAttribute('data-approved', isApproved);
    
    // Update icon
    const icon = button.querySelector('i');
    if (isApproved) {
      icon.classList.remove('fa-circle', 'text-muted');
      icon.classList.add('fa-circle-check', 'text-success');
    } else {
      icon.classList.remove('fa-circle-check', 'text-success');
      icon.classList.add('fa-circle', 'text-muted');
    }
    
    // Update hidden input
    const inputId = this.config.toggles.approval.inputIdPrefix + itemId;
    const input = document.getElementById(inputId);
    if (input) {
      input.value = isApproved;
    }
  }

  toggleTransaction(button, itemId) {
    if (!this.config.toggles.transaction.enabled) return;
    
    const addTransaction = button.getAttribute('data-transaction') === 'false';
    button.setAttribute('data-transaction', addTransaction);
    
    // Update icon
    const icon = button.querySelector('i');
    if (addTransaction) {
      icon.classList.remove('fa-circle', 'text-muted');
      icon.classList.add('fa-circle-check', 'text-success');
      
      // If setting add_transaction to true, ensure is_approved is also true
      const approvalButton = button.closest('.d-flex').previousElementSibling.querySelector('.approval-toggle');
      if (approvalButton && approvalButton.getAttribute('data-approved') === 'false') {
        approvalButton.setAttribute('data-approved', 'true');
        approvalButton.querySelector('i').classList.remove('fa-circle', 'text-muted');
        approvalButton.querySelector('i').classList.add('fa-circle-check', 'text-success');
        const approvalInputId = this.config.toggles.approval.inputIdPrefix + itemId;
        const approvalInput = document.getElementById(approvalInputId);
        if (approvalInput) {
          approvalInput.value = 'true';
        }
      }
    } else {
      icon.classList.remove('fa-circle-check', 'text-success');
      icon.classList.add('fa-circle', 'text-muted');
    }
    
    // Update hidden input
    const inputId = this.config.toggles.transaction.inputIdPrefix + itemId;
    const input = document.getElementById(inputId);
    if (input) {
      input.value = addTransaction;
    }
  }
}

